import React, { useMemo, useState } from 'react';
import {
  AlertTriangle, Users, UserCheck, HelpCircle,
  ChevronDown, ChevronUp, ShieldAlert, TrendingUp,
  Calendar, Heart, Thermometer, CalendarOff, BarChart3,
  Building2, Flame,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { formatDate, formatWeekRange, formatHoursMinutes } from '../../utils/dateUtils';
import { CountBadge } from '../UI/CountBadge';
import { ClassificationBadge } from '../UI/ClassificationBadge';
import { getDepartmentIcon, getDepartmentColor } from '../../config/departmentConfig';
import { getBurnoutPriorityScore, getBurnoutRisk, getBurnoutRiskMeta } from './burnoutUtils';

// ---------------------------------------------------------------------------
// Department sub-tab selector
// ---------------------------------------------------------------------------

function DepartmentTabs({ departments, selected, onChange }) {
  if (!departments || departments.length <= 1) return null;

  return (
    <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
      {departments.map((dept) => {
        const isActive = selected === dept.department_id;
        const DeptIcon = getDepartmentIcon(dept.department_name);
        const dc = getDepartmentColor(dept.department_name);
        return (
          <button
            key={dept.department_id}
            onClick={() => onChange(dept.department_id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200
              focus-visible:outline-none focus-visible:ring-2 ${dc.ring} focus-visible:ring-offset-1
              ${isActive
                ? `${dc.solid} text-white shadow-sm`
                : `${dc.bg} ${dc.text} hover:opacity-80`
              }`}
            title={dept.department_name}
          >
            <DeptIcon className="w-3.5 h-3.5" />
            {dept.department_name}
            <CountBadge count={dept.member_count} theme="employee" active={isActive} className="ml-0.5" />
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary cards for a single department
// ---------------------------------------------------------------------------

function DeptSummaryCards({ dept }) {
  const totalMembers = dept?.member_count ?? 0;
  const roCount = (dept?.ro_teams ?? []).length;
  const unassigned = dept?.unassigned?.member_count ?? 0;

  const alertCount = (dept?.ro_teams ?? []).reduce(
    (sum, t) => sum + (t.compliance_alert_count || 0), 0
  );
  const totalUnaccounted = (dept?.ro_teams ?? []).reduce(
    (sum, t) => sum + (t.unaccounted_count || 0), 0
  );

  const cards = [
    {
      label: 'Members',
      value: totalMembers,
      icon: Users,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-700',
    },
    {
      label: 'Reporting Officers',
      value: roCount,
      sub: unassigned > 0 ? `${unassigned} unassigned` : null,
      icon: UserCheck,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-700',
    },
    {
      label: 'Unaccounted Today',
      value: totalUnaccounted,
      sub: totalUnaccounted > 0 ? 'Needs attention' : 'All accounted for',
      icon: HelpCircle,
      iconBg: totalUnaccounted > 0 ? 'bg-red-50' : 'bg-green-50',
      iconColor: totalUnaccounted > 0 ? 'text-red-600' : 'text-green-600',
    },
    {
      label: 'Compliance Alerts',
      value: alertCount,
      sub: alertCount > 0 ? 'OT / Rest Day alerts' : 'No active alerts',
      icon: ShieldAlert,
      iconBg: alertCount > 0 ? 'bg-amber-50' : 'bg-green-50',
      iconColor: alertCount > 0 ? 'text-amber-600' : 'text-green-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.label} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center">
              <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${c.iconBg}`}>
                <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${c.iconColor}`} />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{c.value}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">{c.label}</p>
              </div>
            </div>
            {c.sub && (
              <p className="text-xs text-gray-400 mt-2 truncate">{c.sub}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RO Team card — expandable to show members
// ---------------------------------------------------------------------------

function ROTeamCard({ team, onViewIndividual }) {
  const [expanded, setExpanded] = useState(false);
  const hasAlerts = (team.compliance_alert_count || 0) > 0;
  const hasUnaccounted = (team.unaccounted_count || 0) > 0;

  const attendancePct = team.attendance_rate_today != null
    ? `${Math.round(team.attendance_rate_today * 100)}%`
    : '—';

  const alPct = team.avg_al_utilisation != null
    ? `${Math.round(team.avg_al_utilisation * 100)}%`
    : '—';

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm transition-shadow hover:shadow-md ${
        hasAlerts || hasUnaccounted
          ? 'border-l-4 border-l-amber-400 border-t border-r border-b'
          : 'border-gray-200'
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded-2xl"
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-900 text-sm truncate">{team.ro_name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{team.member_count} member{team.member_count !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
            {hasAlerts && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-600/20">
                <ShieldAlert className="w-3 h-3" />
                {team.compliance_alert_count}
              </span>
            )}
            {hasUnaccounted && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 ring-1 ring-red-600/20">
                <HelpCircle className="w-3 h-3" />
                {team.unaccounted_count}
              </span>
            )}
            {expanded
              ? <ChevronUp className="w-4 h-4 text-gray-400" />
              : <ChevronDown className="w-4 h-4 text-gray-400" />
            }
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mt-3">
          <MetricCell label="Present" value={team.present_count} />
          <MetricCell label="On Leave" value={team.on_leave_count} />
          <MetricCell
            label="Avg Hours"
            value={team.avg_daily_hours != null ? formatHoursMinutes(team.avg_daily_hours) : '—'}
          />
          <MetricCell label="Attendance" value={attendancePct} />
        </div>
      </button>

      {expanded && (
        <div className="border-t px-4 pb-4">
          <div className="flex items-center justify-between mt-3 mb-2">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Members</p>
            <p className="text-xs text-gray-400">AL Util: {alPct}</p>
          </div>
          <div className="space-y-1.5">
            {(team.members || []).map((m) => (
              <div
                key={m.staff_id}
                className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-gray-900 truncate">{m.full_name}</span>
                  <ClassificationBadge classification={m.classification} />
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewIndividual(m.staff_id);
                  }}
                  className="text-xs text-blue-700 hover:text-blue-900 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded px-1.5 py-0.5"
                >
                  View
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCell({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-sm font-semibold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unassigned members card
// ---------------------------------------------------------------------------

function UnassignedCard({ data, onViewIndividual }) {
  const [expanded, setExpanded] = useState(false);
  const count = data?.member_count ?? 0;
  if (count === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-dashed border-gray-300 shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded-2xl"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-gray-700 text-sm">No Reporting Officer Assigned</p>
            <p className="text-xs text-gray-500 mt-0.5">{count} member{count !== 1 ? 's' : ''}</p>
          </div>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-gray-400" />
            : <ChevronDown className="w-4 h-4 text-gray-400" />
          }
        </div>
      </button>

      {expanded && (
        <div className="border-t px-4 pb-4">
          <div className="space-y-1.5 mt-3">
            {(data.members || []).map((m) => (
              <div
                key={m.staff_id}
                className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="text-sm text-gray-900 truncate">{m.full_name}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewIndividual(m.staff_id);
                  }}
                  className="text-xs text-blue-700 hover:text-blue-900 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded px-1.5 py-0.5"
                >
                  View
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status overview — filtered to one department
// ---------------------------------------------------------------------------

function DeptStatusOverview({ overviewData, isLoading, selectedDeptId }) {
  const filteredMembers = useMemo(() => {
    const all = overviewData?.members ?? [];
    if (!selectedDeptId) return all;
    return all.filter((m) => m.department_id === selectedDeptId);
  }, [overviewData, selectedDeptId]);

  const statusGroups = useMemo(() => {
    if (!filteredMembers.length) return {};
    const groups = {};
    for (const m of filteredMembers) {
      const roName = m.reporting_officer_name || 'Unassigned';
      if (!groups[roName]) {
        groups[roName] = { on_site: 0, on_leave: 0, wfh: 0, unaccounted: 0, total: 0 };
      }
      groups[roName].total += 1;
      if (m.composite_status === 'on_site') groups[roName].on_site += 1;
      else if (m.composite_status === 'on_leave') groups[roName].on_leave += 1;
      else if (m.composite_status === 'work_from_home') groups[roName].wfh += 1;
      else if (m.composite_status === 'unaccounted') groups[roName].unaccounted += 1;
    }
    return groups;
  }, [filteredMembers]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 rounded-2xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  const total = filteredMembers.length;
  if (total === 0) return null;

  const onSite = filteredMembers.filter((m) => m.composite_status === 'on_site').length;
  const wfh = filteredMembers.filter((m) => m.composite_status === 'work_from_home').length;
  const onLeave = filteredMembers.filter((m) => m.composite_status === 'on_leave').length;
  const unaccounted = filteredMembers.filter((m) => m.composite_status === 'unaccounted').length;

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-4 h-4 text-blue-700" />
        <h4 className="text-sm font-semibold text-gray-900">Today's Status</h4>
      </div>

      <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 mb-3">
        {onSite > 0 && (
          <div className="bg-green-500 transition-all" style={{ width: `${(onSite / total) * 100}%` }} title={`Present: ${onSite}`} />
        )}
        {wfh > 0 && (
          <div className="bg-blue-400 transition-all" style={{ width: `${(wfh / total) * 100}%` }} title={`WFH: ${wfh}`} />
        )}
        {onLeave > 0 && (
          <div className="bg-amber-400 transition-all" style={{ width: `${(onLeave / total) * 100}%` }} title={`On leave: ${onLeave}`} />
        )}
        {unaccounted > 0 && (
          <div className="bg-red-400 transition-all" style={{ width: `${(unaccounted / total) * 100}%` }} title={`Unaccounted: ${unaccounted}`} />
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Present {onSite}</span>
        {wfh > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" /> WFH {wfh}</span>}
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> On leave {onLeave}</span>
        {unaccounted > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /> Unaccounted {unaccounted}</span>}
      </div>

      {Object.keys(statusGroups).length > 1 && (
        <>
        {/* Mobile card view */}
        <div className="mt-4 md:hidden space-y-3">
          {Object.entries(statusGroups)
            .sort((a, b) => b[1].unaccounted - a[1].unaccounted || a[0].localeCompare(b[0]))
            .map(([name, counts]) => (
              <div key={name} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                    <p className="text-xs text-gray-500">{counts.total} member{counts.total !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex-shrink-0">
                    {counts.unaccounted > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 ring-1 ring-red-600/20">
                        <HelpCircle className="w-3 h-3" />
                        {counts.unaccounted} unaccounted
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 ring-1 ring-green-600/20">
                        All accounted
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Present</span>
                    <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{counts.on_site}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">On Leave</span>
                    <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{counts.on_leave}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">WFH</span>
                    <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{counts.wfh}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500 flex-shrink-0">Unaccounted</span>
                    <span className={`text-right truncate min-w-0 tabular-nums font-medium ${counts.unaccounted > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {counts.unaccounted}
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Desktop table view */}
        <div className="mt-4 hidden md:block overflow-x-auto">
          <table className="w-full text-xs min-w-[400px]">
            <thead>
              <tr className="border-b text-gray-500 text-left">
                <th className="py-1.5 pr-2 font-medium">Reporting Officer</th>
                <th className="py-1.5 px-2 font-medium text-center">Present</th>
                <th className="py-1.5 px-2 font-medium text-center">On Leave</th>
                <th className="py-1.5 px-2 font-medium text-center">WFH</th>
                <th className="py-1.5 px-2 font-medium text-center">Unaccounted</th>
                <th className="py-1.5 pl-2 font-medium text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(statusGroups)
                .sort((a, b) => b[1].unaccounted - a[1].unaccounted || a[0].localeCompare(b[0]))
                .map(([name, counts]) => (
                  <tr key={name} className="border-b border-gray-50">
                    <td className="py-1.5 pr-2 text-gray-800 font-medium truncate max-w-[140px]">{name}</td>
                    <td className="py-1.5 px-2 text-center text-gray-700">{counts.on_site}</td>
                    <td className="py-1.5 px-2 text-center text-gray-700">{counts.on_leave}</td>
                    <td className="py-1.5 px-2 text-center text-gray-700">{counts.wfh}</td>
                    <td className={`py-1.5 px-2 text-center font-medium ${counts.unaccounted > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {counts.unaccounted}
                    </td>
                    <td className="py-1.5 pl-2 text-center text-gray-700">{counts.total}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Burnout Watchlist — department-scoped burnout risk summary
// ---------------------------------------------------------------------------

function DeptBurnoutSummary({ trendsData, selectedDeptId }) {
  const filteredMembers = useMemo(() => {
    const all = trendsData?.member_hours_summary ?? [];
    if (!selectedDeptId) return all;
    return all.filter((member) => member.department_id === selectedDeptId);
  }, [selectedDeptId, trendsData]);

  const highRisk = useMemo(
    () => filteredMembers.filter((m) => getBurnoutRisk(m) === 'red'),
    [filteredMembers],
  );
  const watchRisk = useMemo(
    () => filteredMembers.filter((m) => getBurnoutRisk(m) === 'amber'),
    [filteredMembers],
  );
  const stableCount = filteredMembers.length - highRisk.length - watchRisk.length;

  const topRisks = useMemo(() => {
    return [...filteredMembers]
      .filter((m) => getBurnoutRisk(m) !== 'green')
      .sort((a, b) => getBurnoutPriorityScore(b) - getBurnoutPriorityScore(a))
      .slice(0, 3);
  }, [filteredMembers]);

  if (!filteredMembers.length) return null;

  const cards = [
    {
      label: 'High Risk',
      value: highRisk.length,
      sub: highRisk.length > 0 ? 'Immediate review' : 'None flagged',
      iconBg: highRisk.length > 0 ? 'bg-red-100' : 'bg-gray-100',
      iconColor: highRisk.length > 0 ? 'text-red-600' : 'text-gray-500',
    },
    {
      label: 'Watch List',
      value: watchRisk.length,
      sub: watchRisk.length > 0 ? 'Monitor workload' : 'None flagged',
      iconBg: watchRisk.length > 0 ? 'bg-amber-100' : 'bg-gray-100',
      iconColor: watchRisk.length > 0 ? 'text-amber-600' : 'text-gray-500',
    },
    {
      label: 'Stable',
      value: stableCount,
      sub: 'No active burnout flags',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
    },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-5 py-3 border-b bg-gray-50 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-red-600" />
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Burnout Watchlist</h4>
            <p className="text-xs text-gray-400">
              Long-hours pressure across the recent trend window
            </p>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {cards.map((card) => (
            <div key={card.label} className="bg-white rounded-2xl border shadow-sm p-4">
              <div className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${card.iconBg}`}>
                  <Flame className={`w-5 h-5 ${card.iconColor}`} />
                </div>
                <div className="ml-3 min-w-0">
                  <p className="text-xl font-bold text-gray-900">{card.value}</p>
                  <p className="text-sm text-gray-600">{card.label}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">{card.sub}</p>
            </div>
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <h5 className="text-sm font-semibold text-gray-900">Top Risks</h5>
            <span className="text-xs text-gray-400">
              {topRisks.length > 0
                ? 'Ranked by hours, streak, and extended-day frequency'
                : 'No active burnout flags'}
            </span>
          </div>

          {topRisks.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 px-4 py-5 text-sm text-gray-400 text-center">
              No department members are currently flagged for burnout risk.
            </div>
          ) : (
            <div className="space-y-2">
              {topRisks.map((member) => {
                const riskMeta = getBurnoutRiskMeta(getBurnoutRisk(member));
                return (
                  <div
                    key={member.staff_id}
                    className="flex items-start justify-between gap-3 rounded-xl border px-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {member.full_name}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ${riskMeta.pill}`}
                        >
                          <span className={`w-2 h-2 rounded-full ${riskMeta.dot}`} />
                          {riskMeta.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Avg{' '}
                        {member.avg_daily_effective_hours != null
                          ? formatHoursMinutes(member.avg_daily_effective_hours)
                          : '—'}{' '}
                        per day
                        {' · '}
                        {Math.round((member.extended_days_rate ?? 0) * 100)}% extended days
                        {' · '}
                        {member.consecutive_working_days ?? 0} consecutive days
                      </p>
                    </div>
                    <div className="text-xs text-gray-400 capitalize flex-shrink-0">
                      {member.classification}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Department Trends — weekly attendance & hours chart
// ---------------------------------------------------------------------------

function DeptTrendsSummary({ trendsData, trendsLoading }) {
  const weeks = trendsData?.weeks ?? [];

  if (trendsLoading) {
    return <div className="h-48 rounded-2xl bg-gray-100 animate-pulse" />;
  }

  if (!weeks.length) return null;

  const chartData = weeks.map((w) => {
    const staff = w.by_classification?.staff ?? {};
    const worker = w.by_classification?.worker ?? {};
    const dist = w.status_distribution ?? {};
    return {
      week: formatWeekRange(w.week_start),
      'Staff Avg Hours': staff.avg_daily_effective_hours,
      'Worker Avg Hours': worker.avg_daily_effective_hours,
      'Present': dist.on_site ?? 0,
      'On Leave': dist.on_leave ?? 0,
      Unaccounted: dist.unaccounted ?? 0,
    };
  });

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-blue-800" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-800">Weekly Trends</h3>
          <p className="text-xs text-gray-500">
            Last {weeks.length} complete week{weeks.length !== 1 ? 's' : ''} — all departments
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <h4 className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">
            Avg Daily Effective Hours
          </h4>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="week" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} domain={[0, 12]} unit="h" />
              <Tooltip formatter={(v) => [formatHoursMinutes(v), undefined]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="Staff Avg Hours" stroke="#1e40af" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              <Line type="monotone" dataKey="Worker Avg Hours" stroke="#059669" strokeWidth={2} dot={{ r: 2 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div>
          <h4 className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">
            Status Distribution
          </h4>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="week" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Present" stackId="s" fill="#22c55e" radius={[0, 0, 0, 0]} />
              <Bar dataKey="On Leave" stackId="s" fill="#f59e0b" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Unaccounted" stackId="s" fill="#ef4444" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Department Leave Health — filtered to one department
// ---------------------------------------------------------------------------

function DeptLeaveHealth({ leaveData, leaveLoading, selectedDeptId }) {
  const filteredMembers = useMemo(() => {
    const all = leaveData?.members ?? [];
    if (!selectedDeptId) return all;
    return all.filter((m) => m.department_id === selectedDeptId);
  }, [leaveData, selectedDeptId]);

  if (leaveLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 rounded-2xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  const year = leaveData?.year;
  if (!filteredMembers.length) return null;

  const alRates = filteredMembers.map((m) => m.al_utilisation_rate).filter((v) => v != null);
  const mcTotals = filteredMembers.map((m) => m.mc_days_ytd ?? 0);
  const zeroAlCount = filteredMembers.filter((m) => m.al_utilisation_rate === 0 || (m.al_remaining_days != null && m.al_utilisation_rate === 0)).length;
  const lowAlCount = filteredMembers.filter((m) => m.al_remaining_days != null && m.al_remaining_days < 3 && m.al_remaining_days >= 0).length;

  const avgAlUtil = alRates.length ? alRates.reduce((a, b) => a + b, 0) / alRates.length : null;
  const avgMcDays = mcTotals.length ? mcTotals.reduce((a, b) => a + b, 0) / mcTotals.length : 0;

  const cards = [
    {
      icon: Calendar,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-800',
      value: avgAlUtil != null ? `${Math.round(avgAlUtil * 100)}%` : '—',
      label: 'Avg AL Used',
      sub: `${year} year-to-date`,
    },
    {
      icon: Thermometer,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      value: Math.round(avgMcDays * 10) / 10,
      label: 'Avg MC Days / Person',
      sub: `${year} year-to-date`,
    },
    {
      icon: CalendarOff,
      iconBg: zeroAlCount > 0 ? 'bg-red-100' : 'bg-green-100',
      iconColor: zeroAlCount > 0 ? 'text-red-600' : 'text-green-600',
      value: zeroAlCount,
      label: 'Zero AL Taken',
      sub: zeroAlCount > 0 ? 'Wellbeing check needed' : 'All clear',
    },
    {
      icon: AlertTriangle,
      iconBg: lowAlCount > 0 ? 'bg-amber-100' : 'bg-green-100',
      iconColor: lowAlCount > 0 ? 'text-amber-600' : 'text-green-600',
      value: lowAlCount,
      label: '< 3 Days AL Left',
      sub: 'Limited leave buffer',
    },
  ];

  const flaggedMembers = filteredMembers.filter(
    (m) =>
      (m.days_since_last_leave != null && m.days_since_last_leave >= 90) ||
      (m.days_since_last_leave == null && m.last_leave_date == null) ||
      m.mon_fri_mc_flag ||
      (m.al_remaining_days != null && m.al_remaining_days < 3 && m.al_remaining_days >= 0)
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
          <Heart className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-800">Leave Health</h3>
          <p className="text-xs text-gray-500">{filteredMembers.length} members in scope</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center">
                <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${c.iconBg}`}>
                  <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${c.iconColor}`} />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{c.value}</p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">{c.label}</p>
                </div>
              </div>
              {c.sub && <p className="text-xs text-gray-400 mt-2 truncate">{c.sub}</p>}
            </div>
          );
        })}
      </div>

      {flaggedMembers.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <h4 className="text-sm font-semibold text-gray-900">
              Leave Wellbeing Flags
              <span className="ml-2 font-normal text-gray-400 text-xs">
                {flaggedMembers.length} member{flaggedMembers.length !== 1 ? 's' : ''}
              </span>
            </h4>
          </div>
          <div className="space-y-2">
            {flaggedMembers.slice(0, 10).map((m) => {
              const flags = [];
              if (m.days_since_last_leave != null && m.days_since_last_leave >= 90) {
                flags.push({ severity: 'red', text: `${m.days_since_last_leave} days since last leave` });
              } else if (m.days_since_last_leave == null && m.last_leave_date == null) {
                flags.push({ severity: 'red', text: 'No leave taken this year' });
              }
              if (m.mon_fri_mc_flag) {
                flags.push({ severity: 'amber', text: 'Mon/Fri MC pattern' });
              }
              if (m.al_remaining_days != null && m.al_remaining_days < 3 && m.al_remaining_days >= 0) {
                flags.push({ severity: 'amber', text: `${m.al_remaining_days} AL day${m.al_remaining_days !== 1 ? 's' : ''} remaining` });
              }
              return (
                <div key={m.staff_id} className="flex items-center justify-between p-3 border border-gray-200 rounded-xl">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium text-gray-900 truncate">{m.full_name}</span>
                    <ClassificationBadge classification={m.classification} />
                  </div>
                  <div className="flex flex-wrap gap-1 flex-shrink-0 ml-2">
                    {flags.map((f, i) => (
                      <span
                        key={i}
                        className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                          f.severity === 'red'
                            ? 'bg-red-50 text-red-700 ring-1 ring-red-600/20'
                            : 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20'
                        }`}
                      >
                        {f.text}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
            {flaggedMembers.length > 10 && (
              <p className="text-xs text-gray-400 pt-1">
                +{flaggedMembers.length - 10} more member{flaggedMembers.length - 10 !== 1 ? 's' : ''} with flags
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function DepartmentOverview({
  breakdownData,
  breakdownLoading,
  breakdownError,
  overviewData,
  overviewLoading,
  trendsData,
  trendsLoading,
  leaveData,
  leaveLoading,
  onViewIndividual,
}) {
  const departments = breakdownData?.departments ?? [];
  const [selectedDeptId, setSelectedDeptId] = useState(null);

  // Auto-select first department with members
  const activeDeptId = useMemo(() => {
    if (selectedDeptId && departments.find((d) => d.department_id === selectedDeptId)) {
      return selectedDeptId;
    }
    const first = departments.find((d) => d.member_count > 0) ?? departments[0];
    return first?.department_id ?? null;
  }, [selectedDeptId, departments]);

  const activeDept = useMemo(
    () => departments.find((d) => d.department_id === activeDeptId) ?? null,
    [departments, activeDeptId],
  );

  const dateLabel = overviewData?.date ? formatDate(overviewData.date) : null;

  if (breakdownLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 rounded-2xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (breakdownError) {
    const is403 = breakdownError.response?.status === 403;
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
        <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
        <p className="font-medium">{is403 ? 'Access Denied' : 'Failed to load department data'}</p>
        <p className="text-sm mt-1">
          {breakdownError.response?.data?.detail || breakdownError.message}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Department sub-tabs */}
      <DepartmentTabs
        departments={departments}
        selected={activeDeptId}
        onChange={setSelectedDeptId}
      />

      {/* Department header */}
      {activeDept && (() => {
        const ActiveDeptIcon = getDepartmentIcon(activeDept.department_name);
        const adc = getDepartmentColor(activeDept.department_name);
        return (
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-full ${adc.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
            <ActiveDeptIcon className={`w-5 h-5 ${adc.text}`} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {activeDept.department_name}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {activeDept.member_count} member{activeDept.member_count !== 1 ? 's' : ''}
              {dateLabel && <> &middot; Data as of {dateLabel}</>}
            </p>
          </div>
        </div>
        );
      })()}

      {activeDept && activeDept.member_count === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">
          No operational staff in this department.
        </div>
      )}

      {activeDept && activeDept.member_count > 0 && (
        <>
          {/* Summary cards for this department */}
          <DeptSummaryCards dept={activeDept} />

          {/* Burnout watchlist for this department */}
          <DeptBurnoutSummary
            trendsData={trendsData}
            selectedDeptId={activeDeptId}
          />

          {/* Status overview for this department */}
          <DeptStatusOverview
            overviewData={overviewData}
            isLoading={overviewLoading}
            selectedDeptId={activeDeptId}
          />

          {/* RO sub-team breakdown for this department */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-blue-700" />
              <h3 className="text-sm font-semibold text-gray-900">
                Team Breakdown by Reporting Officer
              </h3>
              <span className="text-xs text-gray-400 ml-auto">
                {activeDept.ro_teams.length} RO{activeDept.ro_teams.length !== 1 ? 's' : ''}
              </span>
            </div>

            {activeDept.ro_teams.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No reporting officer teams found in this department.
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {activeDept.ro_teams.map((team) => (
                  <ROTeamCard
                    key={team.ro_id}
                    team={team}
                    onViewIndividual={onViewIndividual}
                  />
                ))}
              </div>
            )}

            <div className="mt-3">
              <UnassignedCard data={activeDept.unassigned} onViewIndividual={onViewIndividual} />
            </div>
          </div>

          {/* Leave health for this department */}
          <DeptLeaveHealth
            leaveData={leaveData}
            leaveLoading={leaveLoading}
            selectedDeptId={activeDeptId}
          />
        </>
      )}

      {/* Trends — aggregate across all departments */}
      <DeptTrendsSummary trendsData={trendsData} trendsLoading={trendsLoading} />
    </div>
  );
}
